import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkOrder, mkType } from '@main/schema-utils';
import { DecryptionContext } from '@main/sombra-types';

import { ProfileOrderField } from './enums';
import { ProfileDataPoint } from './ProfileDataPoint';
import { ProfilePreview } from './profilePreview';
import { RequestIdentifier } from './RequestIdentifier';

export const Profile = mkType({
  name: 'Profile',
  comment:
    'A unique identifier for a data subject in the context of a data silo. From this profile is where all data is attached.',
  fields: {
    ...ProfilePreview.fields,
    // TODO: https://linear.app/transcend/issue/WAL-8664/remove-the-accessrequestprofiles-resolver-and-profile-graphql-type
    requestIdentifiers: {
      comment: 'The request identifiers that mapped to this profile',
      list: true,
      type: RequestIdentifier,
    },
    // TODO: https://linear.app/transcend/issue/WAL-8664/remove-the-accessrequestprofiles-resolver-and-profile-graphql-type
    profileDataPoints: {
      comment:
        'The statuses of the datapoints to fire in respect to this profile',
      list: true,
      type: ProfileDataPoint,
    },
  },
});

/** Override type */
export type Profile = SchemaToType<typeof Profile>;

export const ProfileFiltersInput = mkInput({
  name: 'ProfileFiltersInput',
  comment: 'Input for filtering profiles',
  fields: {
    requestDataSiloIds: {
      comment: 'Filter by RequestDataSilo IDs',
      modelName: 'RequestDataSilo',
      type: 'id',
      optional: true,
      list: true,
    },
    text: {
      comment: 'Search profiles by identifier or type',
      type: 'string',
      optional: true,
    },
    type: {
      comment: 'Filter by profile type',
      type: 'string',
      optional: true,
    },
    hasErrors: {
      comment: 'Filter by profiles with ProfileDataPoint or RequestFile errors',
      type: 'boolean',
      optional: true,
    },
  },
});

/** Override type */
export type ProfileFiltersInput = SchemaToType<typeof ProfileFiltersInput>;

export const ProfileOrder = mkOrder(Profile.name, ProfileOrderField);

/** Override type */
export type ProfileOrder = SchemaToType<typeof ProfileOrder>;

export const BulkUnwrapProfileIdentifiersInput = mkInput({
  name: 'BulkUnwrapProfileIdentifiersInput',
  comment: 'Input to decrypt a list profiles',
  fields: {
    profileIds: {
      comment: 'List of profile Ids to decrypt',
      modelName: 'profile',
      type: 'id',
      list: true,
    },
  },
});

/** Override type */
export type BulkUnwrapProfileIdentifiersInput = SchemaToType<
  typeof BulkUnwrapProfileIdentifiersInput
>;

export const DecryptionContextProfileMapping = mkType({
  name: 'DecryptionContextProfileMapping',
  comment: 'A mapping from profile to decryption context',
  fields: {
    profileId: {
      comment: 'The profile id',
      type: 'id',
      modelName: 'profile',
    },
    decryptionContext: {
      comment: 'The decryption context for the profile',
      type: DecryptionContext,
    },
  },
});

/** Override type */
export type DecryptionContextProfileMapping = SchemaToType<
  typeof DecryptionContextProfileMapping
>;
